// frontend/src/components/admin/AcControlPanel.jsx
//
// Panneau de contrôle pour les associations dont l'actuateur est un Contrôleur IR
// (ex: climatisation pilotée via un Smart WiFi IR Controller L5).
// Logique portée depuis le prototype "clim-control" (public/app.js), adaptée en
// React + appels vers les endpoints /api/associations/:id/ac-* du backend.

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import Loading from '../common/Loading';

const MODE_LABELS_FR = {
  'Cooling mode': 'Froid',
  'Heating mode': 'Chaud',
  'Automatic mode': 'Auto',
  'Air supply mode': 'Ventilation',
  'Dehumidification mode': 'Sec',
};

// ✅ Pictogrammes universels pour les modes — compréhensibles sans savoir lire
const MODE_ICONS = {
  'Cooling mode': '❄️',
  'Heating mode': '🔥',
  'Automatic mode': '🤖',
  'Air supply mode': '🌬️',
  'Dehumidification mode': '💧',
};

function modeLabel(modeEntry) {
  if (!modeEntry) return '';
  return MODE_LABELS_FR[modeEntry.modeName] || modeEntry.modeName || `Mode ${modeEntry.mode}`;
}

function modeIcon(modeEntry) {
  if (!modeEntry) return '❔';
  return MODE_ICONS[modeEntry.modeName] || '⚙️';
}

// ✅ Pictogrammes universels pour les vitesses de ventilateur
function fanIcon(fanName) {
  const name = (fanName || '').toLowerCase();
  if (name.includes('auto')) return '🔄';
  if (name.includes('high') || name.includes('fort') || name.includes('rapide')) return '💨';
  if (name.includes('low') || name.includes('faible') || name.includes('lent')) return '🐢';
  if (name.includes('middle') || name.includes('mid') || name.includes('moyen')) return '🌬️';
  return '🌀';
}

const AcControlPanel = ({ associationId, associationName, endpointBase }) => {
  // ✅ Admin/Owner passent par /associations/:id/ac-* (associationId fourni).
  //    Simple passe par /simple-association/ac-* (pas d'id dans l'URL, résolu
  //    côté backend via le Simple authentifié) — on lui passe endpointBase="/simple-association".
  const base = endpointBase || `/associations/${associationId}`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState({ power: false, temp: 24, mode: 0, wind: 0 });

  // ✅ ac-config décrit les modes/températures disponibles : statique, ne
  //    change jamais en cours d'utilisation. On évite de le repolliner toutes
  //    les 15s avec ac-status (ça doublait inutilement les appels Tuya).
  const loadConfig = useCallback(async () => {
    try {
      const configRes = await api.get(`${base}/ac-config`);
      setConfig(configRes.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Impossible de charger la climatisation');
    }
  }, [base]);

  const loadStatus = useCallback(async () => {
    try {
      const statusRes = await api.get(`${base}/ac-status`);
      const raw = statusRes.data.data || {};
      setStatus({
        power: raw.power === true || raw.power === '1' || raw.power === 1,
        temp: Number(raw.temp) || 24,
        mode: Number(raw.mode) || 0,
        wind: Number(raw.wind) || 0,
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Impossible de charger la climatisation');
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 15000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  // Conservé pour les actions qui doivent rafraîchir immédiatement après une commande.
  const load = loadStatus;

  const getModeEntry = (modeValue) => {
    if (!config) return null;
    return config.modes.find((m) => m.mode === Number(modeValue)) || config.modes[0] || null;
  };

  const getTempEntry = (modeEntry, tempValue) => {
    if (!modeEntry?.temps?.length) return null;
    return modeEntry.temps.find((t) => t.temp === Number(tempValue)) || modeEntry.temps[0];
  };

  const currentModeEntry = getModeEntry(status.mode);
  const currentTempEntry = getTempEntry(currentModeEntry, status.temp);

  const runAction = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la commande');
    } finally {
      setBusy(false);
    }
  };

  const handlePower = (on) =>
    runAction(async () => {
      await api.post(`${base}/control`, { action: on ? 'on' : 'off' });
      setStatus((s) => ({ ...s, power: on }));
    });

  const handleMode = (modeValue) =>
    runAction(async () => {
      const res = await api.post(`${base}/ac-command`, { command: 'mode', value: modeValue });
      setStatus((s) => ({
        ...s,
        mode: res.data.data.mode,
        temp: res.data.data.temp ?? s.temp,
        wind: res.data.data.wind ?? s.wind,
      }));
    });

  const handleTemp = (tempValue) =>
    runAction(async () => {
      const res = await api.post(`${base}/ac-command`, { command: 'temp', value: tempValue });
      setStatus((s) => ({ ...s, temp: res.data.data.temp, wind: res.data.data.wind ?? s.wind }));
    });

  const handleWind = (windValue) =>
    runAction(async () => {
      const res = await api.post(`${base}/ac-command`, { command: 'wind', value: windValue });
      setStatus((s) => ({ ...s, wind: res.data.data.wind }));
    });

  const stepTemp = (direction) => {
    if (!currentModeEntry?.temps?.length) return;
    const temps = currentModeEntry.temps.map((t) => t.temp).sort((a, b) => a - b);
    const currentIndex = temps.indexOf(Number(status.temp));
    let nextIndex = currentIndex === -1 ? 0 : currentIndex + direction;
    nextIndex = Math.max(0, Math.min(temps.length - 1, nextIndex));
    handleTemp(temps[nextIndex]);
  };

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
        ❌ {error}
        <button onClick={load} className="ml-2 underline">Réessayer</button>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-xl p-5 text-white">
      <div className="text-center mb-4">
        <p className="text-xs uppercase tracking-wide text-gray-400">Contrôleur IR L5</p>
        <h3 className="text-lg font-bold">{associationName || 'Climatisation'}</h3>
      </div>

      {/* Température */}
      <div className="flex items-center justify-center gap-6 mb-5">
        <button
          onClick={() => stepTemp(-1)}
          disabled={busy}
          className="w-10 h-10 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-xl font-bold"
        >
          −
        </button>
        <div className="text-4xl font-bold">
          {Math.round(status.temp)}<span className="text-lg align-top">°C</span>
        </div>
        <button
          onClick={() => stepTemp(1)}
          disabled={busy}
          className="w-10 h-10 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-xl font-bold"
        >
          +
        </button>
      </div>

      {/* Power */}
      <div className="flex justify-center mb-5">
        <button
          onClick={() => handlePower(!status.power)}
          disabled={busy}
          className={`px-6 py-2 rounded-full font-semibold transition ${
            status.power ? 'bg-emerald-500 text-white' : 'bg-gray-700 text-gray-300'
          } disabled:opacity-50`}
        >
          ⏻ {status.power ? 'Allumé' : 'Éteint'}
        </button>
      </div>

      {/* Modes */}
      <div className="mb-4">
        <p className="text-xs uppercase text-gray-400 mb-2">Mode</p>
        <div className="flex flex-wrap gap-2">
          {(config?.modes || []).map((m) => (
            <button
              key={m.mode}
              onClick={() => handleMode(m.mode)}
              disabled={busy}
              className={`px-3 py-1.5 rounded-lg text-sm border transition disabled:opacity-50 ${
                m.mode === status.mode
                  ? 'border-emerald-400 text-emerald-300 bg-emerald-400/10'
                  : 'border-gray-600 text-gray-300 hover:border-gray-400'
              }`}
            >
              {modeIcon(m)} {modeLabel(m)}
            </button>
          ))}
        </div>
      </div>

      {/* Ventilateur */}
      <div>
        <p className="text-xs uppercase text-gray-400 mb-2">Ventilateur</p>
        <div className="flex flex-wrap gap-2">
          {(currentTempEntry?.fans || []).length === 0 && (
            <span className="text-sm text-gray-500">Non réglable dans ce mode</span>
          )}
          {(currentTempEntry?.fans || []).map((f) => (
            <button
              key={f.fan}
              onClick={() => handleWind(f.fan)}
              disabled={busy}
              className={`px-3 py-1.5 rounded-lg text-sm border transition disabled:opacity-50 ${
                f.fan === status.wind
                  ? 'border-emerald-400 text-emerald-300 bg-emerald-400/10'
                  : 'border-gray-600 text-gray-300 hover:border-gray-400'
              }`}
            >
              {fanIcon(f.fanName)} {f.fanName || `V${f.fan}`}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-5 text-center">
        Modes et vitesses affichés = ceux réellement supportés par la télécommande IR.
      </p>
    </div>
  );
};

export default AcControlPanel;
